//@flow
import * as React from 'react';
import debounce from 'lodash/debounce';

import { VirtualizedSelectWithFooter } from 'common/components/core/select';
import { Contact } from 'common/models/internal/Contact';
import {
    FilterBuilder,
    FilterOperation,
    LogicalOperation
} from 'common/models/internal/Filter';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';

import { bulkContactFetch as bulkContactFetchApi } from 'common/middleware/api';
import { translate } from 'react-i18next';

import type { TeamId } from 'common/models/internal/TeamId';
import type { List } from 'immutable';

type Props = {
    clientId: string,
    teamIds?: List<TeamId>,
    name: string,
    contact: ?Contact,
    theme: Object,
    t: (string, ?Object) => string,
    onContactChange: (?Contact) => void,
    footer?: React.Node
};

type State = {
    isLoading: boolean,
    contacts: Array<Contact>,
    searchTerm: ?string
};

class ContactInput extends React.Component<Props, State> {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: false,
            contacts: [],
            searchTerm: null
        };

        this.onInputChange = debounce(this.onInputChange, 300);
    }

    componentDidMount() {
        const { clientId } = this.props;
        this.setState({
            isLoading: true
        });
        this.queryContactsSearchApi(clientId, []);
    }

    queryContactsSearchApi = (clientIdToRequest, filters) => {
        bulkContactFetchApi
            .post(
                { clientId: clientIdToRequest },
                {
                    filters
                }
            )
            .then((response: Object) => {
                const { contacts } = response;
                const contactsArray: Array<Contact> = contacts.map(
                    contactJson => Contact.fromJS(contactJson)
                );

                this.setState({
                    isLoading: false,
                    contacts: contactsArray
                });
            });
    };

    fetchContactsWithNamePhoneNumberFilter = (
        clientIdToRequest: string,
        value: string
    ): void => {
        const nameFilterBuilder = new FilterBuilder()
            .withName('name')
            .withOperation(FilterOperation.ContainsToken)
            .withValue(value)
            .withExpressionLogicalOperation(LogicalOperation.OR);
        const phoneNumberFilterBuilder = new FilterBuilder()
            .withName('phoneNumber.phoneNumber')
            .withOperation(FilterOperation.ContainsToken)
            .withValue(value)
            .withExpressionLogicalOperation(LogicalOperation.OR);
        const namePhoneNumberFilters = new FilterListBuilder()
            .add(phoneNumberFilterBuilder)
            .add(nameFilterBuilder);
        this.queryContactsSearchApi(
            clientIdToRequest,
            namePhoneNumberFilters.build()
        );
    };

    fetchContacts = (value: ?string): void => {
        const { clientId: clientIdToRequest } = this.props;

        if (value) {
            this.fetchContactsWithNamePhoneNumberFilter(
                clientIdToRequest,
                value
            );
            return;
        } else {
            this.queryContactsSearchApi(clientIdToRequest, []);
        }
    };

    onInputChange = (input: string) => {
        this.setState(
            {
                isLoading: true,
                searchTerm: input.toString().trim()
            },
            () => this.fetchContacts(input.toString().trim())
        );
    };

    onOpen = () => {
        this.setState(
            {
                isLoading: true,
                searchTerm: null
            },
            () => this.fetchContacts()
        );
    };

    getOptionValue = (option: Contact) => {
        return option.id;
    };

    getOptionLabel = (option: Contact) => {
        return option.name;
    };

    getOptionMeta = (option: Contact) => {
        return option.phoneNumber?.phoneNumber;
    };

    getNoOptionsMessage = () => {
        const { t } = this.props;
        return t('contactInput.noContactFound');
    };

    render() {
        const { contact, theme, onContactChange, t, footer } = this.props;
        const { contacts, isLoading } = this.state;
        return (
            <VirtualizedSelectWithFooter
                doubleLine
                theme={theme}
                cache={false}
                openMenuOnFocus
                optionHeight={41}
                isClearable
                getOptionLabel={this.getOptionLabel}
                getOptionValue={this.getOptionValue}
                getOptionMeta={this.getOptionMeta}
                isLoading={isLoading}
                noOptionsMessage={isLoading ? false : this.getNoOptionsMessage}
                /* This is required, because
                   the defaultProp for filterOptions={defaultFilterOptions}
                   in react-select, will not let standalone options to render.*/
                filterOption={null}
                options={contacts}
                onInputChange={this.onInputChange}
                value={contact}
                onMenuOpen={this.onOpen}
                placeholder={t('contactInput.searchText')}
                onChange={onContactChange}
                noMarginBottom
                maxMenuHeight={190}
                footer={footer}
            />
        );
    }
}

export default translate('entityManagement')(ContactInput);
